/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { I18nMessage, useFormatter, useTranslations } from "@i18n/react";
import type { TriviaGame } from "@packages/sdk";
import { useMemo } from "react";

import {
  constantSemanticColors,
  spacing,
  type ThemeType,
} from "../../../../../global/emotion";
import type { CardProps } from "../../../refactor/Card/Card.emotion";
import { Card } from "../../../refactor/Card/Card.emotion";
import { Text } from "../../_core/Text/Text.emotion";
import { TriviaAnswerScore } from "./TriviaScore.emotion";
import { TriviaShareIconButton } from "./TriviaShareIconButton";

const cardStyle: Interpolation = {
  paddingBottom: "96px",
  paddingTop: `calc(${spacing.full} - ${spacing.xs})`,
};
const imgContainerStyle: Interpolation<{
  color: TriviaMediumProps["color"];
}> = (props) => ({
  alignItems: "center",
  backgroundColor: props.color,
  display: "flex",
  justifyContent: "center",
});
const infoContainerStyle: Interpolation = {
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
};
const maskStyle: Interpolation = {
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
};

const cssDivIsFinishedContentContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  color: theme.colors.white,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.l,
  height: "100%",
  justifyContent: "space-between",
  padding: theme.spacing.l,
});
const cssDivContentContainerGroupContainer: Interpolation<ThemeType> = (
  theme,
) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing.s,
  justifyContent: "space-between",
  width: "100%",
});
const cssDivMatrixContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.m,
});
const cssTextBody: Interpolation<ThemeType> = (theme) => ({
  color: theme.tints.tint80,
  textAlign: "center",
});
const scoresContainerStyle: Interpolation = {
  alignItems: "center",
  alignSelf: "stretch",
  display: "flex",
  gap: "15px",
  justifyContent: "center",
};
const summaryContainerStyle: Interpolation = {
  display: "flex",
  gap: spacing.s,
  justifyContent: "space-around",
  paddingInline: "15px",
  width: spacing.full,
};
const summaryStatisticContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: "5px",
  textAlign: "center",
};
const summaryStatisticLabelStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  whiteSpace: "nowrap",
};

export type TriviaMediumProps = CardProps & { game: TriviaGame };

export const TriviaMedium = ({
  centerActions,
  color,
  description,
  imgContainerCSS = {},
  imgSrc,
  infoContainerCSS = {},
  maskProps,
  title,
  game,
  ...props
}: TriviaMediumProps) => {
  const t = useTranslations();
  const format = useFormatter();
  const questions = game?.questions ?? [];
  const summary = game?.summary ?? null;
  const userAnswers = useMemo(
    () =>
      questions.map((question) => {
        const correctAnswer = question.answers.find((answer) => answer.correct);
        const userAnswer = question.user_answer;

        return {
          correct: correctAnswer?.id === userAnswer?.answer_id,
          questionId: question.id,
        };
      }),
    [questions],
  );
  const numCorrect = useMemo(
    () => userAnswers.reduce((prev, cur) => prev + (cur.correct ? 1 : 0), 0),
    [userAnswers],
  );
  return (
    <Card
      centerActions={questions && summary ? null : centerActions}
      color={color}
      description={
        questions && summary ? null : (description ?? t("trivia_sub_title"))
      }
      imgContainerCSS={[imgContainerStyle({ color }), imgContainerCSS]}
      imgSrc={questions && summary ? null : imgSrc}
      infoContainerCSS={[infoContainerStyle, infoContainerCSS]}
      maskCSS={questions && summary ? { display: "none" } : maskStyle}
      maskProps={maskProps}
      overlay={
        questions && summary ? (
          <div css={cssDivIsFinishedContentContainer}>
            <div css={cssDivContentContainerGroupContainer}>
              <Text size="l" type="title">
                <I18nMessage message="trivia_game_finished_title" />
              </Text>
              <TriviaShareIconButton game={game} />
            </div>
            <div css={cssDivMatrixContainer}>
              <div key="scoresContainer" css={scoresContainerStyle}>
                {userAnswers.map((userAnswer, index) => (
                  <TriviaAnswerScore
                    key={`${userAnswer.questionId}_${index}`}
                    {...userAnswer}
                    index={index}
                    size="m"
                  />
                ))}
              </div>
              <Text css={cssTextBody} size="m" type="body">
                <I18nMessage
                  message={
                    numCorrect > 0
                      ? "trivia_result_subtitle"
                      : "trivia_result_subtitle_none_correct"
                  }
                  values={numCorrect > 0 ? { 0: numCorrect } : undefined}
                />
              </Text>
            </div>
            <div key="summaryContainer" css={summaryContainerStyle}>
              {[
                {
                  label: t("trivia_summary_seconds"),
                  value: format.number(summary.total_seconds_elapsed),
                },
                {
                  label: t("trivia_current_streak"),
                  value: format.number(summary.streak),
                },
                {
                  label: t("trivia_max_streak"),
                  value: format.number(summary.longest_streak),
                },
              ].map((statistic) => (
                <div key={statistic.label} css={summaryStatisticContainerStyle}>
                  <Text type="title" size="l">
                    {statistic.value}
                  </Text>
                  <Text
                    type="detail"
                    size="xl"
                    css={summaryStatisticLabelStyle}
                  >
                    {statistic.label}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        ) : null
      }
      title={questions && summary ? null : (title ?? t("trivia_section_title"))}
      css={cardStyle}
      {...props}
    />
  );
};
